import type { FC, ReactElement } from "react"

import type { QueryKey } from "@tanstack/react-query"
import { m } from "framer-motion"
import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import isNumber from "lodash-es/isNumber"
import isString from "lodash-es/isString"
import isUndefined from "lodash-es/isUndefined"
import map from "lodash-es/map"

import {
    SessionInfo,
    type TSessionInfoRenderActionsPropsFunction,
    type TSessionInfoRenderButtonPropsFunction,
    type TSessionInfoRenderMessagesPropsFunction,
    mapSessionExtraPartyModelsToSessionInfoExtraParties as extraPartiesAdapter,
    getSessionInfoColorSchema,
    renderSessionInfoAttendanceModalOpener as renderAttendanceModalOpener,
    renderSessionInfoCancelledSessionModalOpener as renderCancelledSessionModalOpener,
    renderSessionInfoHeaderAvatar as renderHeaderAvatar,
    renderSessionInfoHeaderChevron as renderHeaderChevron,
    renderSessionInfoHeaderDropdown as renderHeaderDropdown,
    renderSessionInfoHeaderNameTooltip as renderHeaderNameTooltip,
    renderSessionInfoPostSessionModalOpener as renderPostSessionModalOpener,
    renderSessionInfoAttendanceListItem,
    renderSessionInfoNotice
} from "@/3514/components"
import { sessionTimeStateUtils } from "@/3514/hooks"
import { EAppAnimationName } from "@/3514/store/app"
import { ESessionModality, ESessionTypeSlug, EUserRole, type ICoachingSessionModel } from "@/3514/types"
import {
    getTranslatedSessionName,
    mapCoachingSessionsParticipantsToSessionInfoParticipants as participantsMapper
} from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { type TEmptyAsyncCallback, type TEmptyRenderCallback } from "@/shared/types/functions"

import uiHooks from "../hooks"
import { type TCoachingSessionsEntityProps as TEntityProps } from "../types"

type _TProps = {
    user: TEntityProps["user"]
    sessionsUpdatedAt?: number
    sessions: ICoachingSessionModel[]
    sessionsQueryKey: QueryKey
    sessionInfoRole: EUserRole
    animationKey: EAppAnimationName
    animationDelay: number
    expandedAccordionIndexes: number[]
    containerClassName: string
    onLiveSessionStarted: TEmptyAsyncCallback
    onLiveSessionPassed: TEmptyAsyncCallback
    onAttendeesListHover(participantId: number, sessionModality: ESessionModality): Promise<void>
    renderTitle: TEmptyRenderCallback
    renderEmptyMessage: TEmptyRenderCallback
    renderAccordionButtonsProps: TSessionInfoRenderButtonPropsFunction
    renderAccordionActionsProps: TSessionInfoRenderActionsPropsFunction
    renderAccordionMessagesProps: TSessionInfoRenderMessagesPropsFunction
    withSkippedRenderOnEmptyItems: boolean
    withReschedulableSessions: boolean
    withGiftSessions: boolean
    withAttendanceMarkableSessions: boolean
    withSwappableSessions: boolean
    withPostSessionModalOpeners: boolean
    withSessionsLiveChecker: boolean
}
const List: FC<_TProps> = ({
    user,
    sessionsUpdatedAt,
    sessions,
    sessionsQueryKey,
    sessionInfoRole,
    animationKey,
    animationDelay,
    expandedAccordionIndexes,
    containerClassName,
    onLiveSessionStarted,
    onLiveSessionPassed,
    onAttendeesListHover,
    renderTitle,
    renderEmptyMessage,
    renderAccordionButtonsProps,
    renderAccordionActionsProps,
    renderAccordionMessagesProps,
    withSkippedRenderOnEmptyItems,
    withReschedulableSessions,
    withGiftSessions,
    withAttendanceMarkableSessions,
    withSwappableSessions,
    withPostSessionModalOpeners,
    withSessionsLiveChecker
}: _TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        controls,
        containerRef,
        isMotionReduced,
        isMobile,
        wasAnimated
    }: ReturnType<typeof uiHooks.useListAnimation> = uiHooks.useListAnimation({
        animationName: animationKey,
        updateKey: sessionsUpdatedAt
    })

    return (
        <m.div
            ref={containerRef}
            hidden={isEmpty(sessions) ? withSkippedRenderOnEmptyItems : false}
            className={containerClassName}
            animate={controls}
            initial={wasAnimated ? false : "hidden"}
            layout={!isMobile}
            variants={{
                hidden: { opacity: 0 },
                shown: {
                    opacity: 1,
                    transition: {
                        staggerChildren: 0.045,
                        when: "beforeChildren",
                        duration: isMotionReduced ? 0 : 0.25,
                        delayChildren: 0.08,
                        delay: animationDelay
                    }
                }
            }}
        >
            {renderTitle()}

            {isEmpty(sessions)
                ? renderEmptyMessage()
                : map<ICoachingSessionModel, ReactElement>(
                      sessions,
                      (s: ICoachingSessionModel, idx: number): ReactElement => {
                          const headerSessionData = {
                              sessionId: s.sessionId,
                              moduleId: s.moduleId,
                              moduleTitle: s.moduleTitle,
                              isSessionCancelled: s.isCancelled,
                              isSessionAttendanceMarked: s.isAttendanceMarked,
                              isModuleReflectionPassed: s.isModuleReflectionPassed
                          }

                          return (
                              <m.div
                                  key={s.sessionId}
                                  viewport={{ once: false, amount: 0.2 }}
                                  variants={{
                                      hidden: { opacity: 0, y: 10 },
                                      shown: {
                                          opacity: 1,
                                          y: 0,
                                          transition: {
                                              duration: isMotionReduced ? 0 : 0.25,
                                              type: "tween",
                                              ease: "easeOut"
                                          }
                                      }
                                  }}
                              >
                                  <SessionInfo
                                      tag="div"
                                      viewerRole={sessionInfoRole}
                                      isExpandable={!s.isCancelled}
                                      isDefaultExpanded={
                                          isUndefined(expandedAccordionIndexes)
                                              ? false
                                              : includes<number>(expandedAccordionIndexes, idx)
                                      }
                                      withLiveChecker={withSessionsLiveChecker ? !s.isCancelled && idx === 0 : false}
                                      onLiveSessionStarted={onLiveSessionStarted}
                                      onLiveSessionPassed={onLiveSessionPassed}
                                      getColorSchema={getSessionInfoColorSchema}
                                      sessionsQueryKey={sessionsQueryKey}
                                      onAttendeesListHover={onAttendeesListHover}
                                      data={{
                                          session: {
                                              id: s.sessionId,
                                              participants: participantsMapper(s.participants),
                                              minHoursToEdit: s.allowedMinDaysToEdit * 24,
                                              groupId: s.groupId,
                                              joinUrl: s.joinUrl,
                                              duration: s.sessionDuration,
                                              startDate: s.sessionTime,
                                              name: getTranslatedSessionName(s.sessionName, t),
                                              modality: s.modality,
                                              rescheduleCount: s.rescheduleHistory.length,
                                              isMissed: s.isMissed,
                                              isGifted: s.isGifted,
                                              isSwappable: withSwappableSessions
                                                  ? s.isSwapAllowed &&
                                                    !eq(s.sessionSlug, ESessionTypeSlug.IndividualBonus)
                                                  : false,
                                              isReschedulable: withReschedulableSessions
                                                  ? s.isRescheduleAllowed
                                                  : false,
                                              isAttendanceMarked: s.isAttendanceMarked,
                                              isCancelled: s.isCancelled,
                                              isExtraParty: s.isExtraPartySession
                                          },
                                          user: {
                                              id: user.id,
                                              firstName: user.firstName,
                                              lastName: user.lastName,
                                              photo: user.photo
                                          },
                                          extraParties: extraPartiesAdapter(s.extraParties),
                                          coach: {
                                              id: isNumber(s.coachId) ? s.coachId : null,
                                              firstName: isString(s.coach?.firstName) ? s.coach.firstName : null,
                                              photo: isString(s.coach?.photo) ? s.coach.photo : null
                                          }
                                      }}
                                      renderers={{
                                          renderNotice: renderSessionInfoNotice(sessionInfoRole),
                                          renderButtonsProps: renderAccordionButtonsProps,
                                          renderMessagesProps: renderAccordionMessagesProps,
                                          renderAttendanceListItem: renderSessionInfoAttendanceListItem,
                                          renderHeaderDropdown: renderHeaderDropdown(renderAccordionActionsProps),
                                          renderHeaderAvatar: renderHeaderAvatar(s.modality, {
                                              isGuest: s.isExtraPartySession,
                                              isGifted: withGiftSessions ? s.isGifted : false,
                                              size: 45
                                          }),
                                          renderHeaderChevron: renderHeaderChevron({
                                              isSessionInfoExpandable: !s.isCancelled
                                          }),
                                          renderHeaderNameToolip: renderHeaderNameTooltip({
                                              sessionId: s.sessionId,
                                              sessionName: s.sessionName
                                          }),
                                          renderPostSessionModalOpener: renderPostSessionModalOpener({
                                              isEnabled: withPostSessionModalOpeners ? !s.isMissed : false,
                                              user,
                                              sessionsQueryKey,
                                              session: headerSessionData
                                          }),
                                          renderCancelledSessionModalOpener: renderCancelledSessionModalOpener({
                                              session: headerSessionData,
                                              sessionsQueryKey
                                          }),
                                          renderAttendanceModalOpener: renderAttendanceModalOpener({
                                              isEnabled:
                                                  withAttendanceMarkableSessions &&
                                                  !s.isCancelled &&
                                                  !s.isAttendanceMarked &&
                                                  sessionTimeStateUtils.getIsSessionPast({
                                                      startDate: s.sessionTime,
                                                      durationInMins: s.sessionDuration
                                                  }),
                                              session: {
                                                  modality: s.modality,
                                                  id: s.sessionId,
                                                  startDate: s.sessionTime,
                                                  minHoursToEdit: s.allowedMinDaysToEdit * 24
                                              },
                                              userId: user.id,
                                              sessionsQueryKey,
                                              groupId: s.groupId
                                          })
                                      }}
                                  />
                              </m.div>
                          )
                      }
                  )}
        </m.div>
    )
}

List.displayName = "CoachingSessionsList"

export default List
